/**
 * Proof-of-play (API.md, POST /api/v3/plays).
 *  - PlayRecorder: ascolta gli eventi show/hide e produce un record per ogni contenuto andato in onda
 *    (fallback compreso). started_at = istante del `show` (orologio corretto con l'offset),
 *    duration_ms = tempo reale fino all'`hide` (clock monotono).
 *  - PlayBuffer: buffer persistente (KvStore/IndexedDB), max 10.000 (si scartano i più vecchi),
 *    idempotente sull'id.
 *  - PlayReporter: invia a lotti da 500; un lotto si cancella SOLO dopo un 200.
 */
import type { EventSink, PlayerEventType } from '../diag';
import type { KvStore } from '../store/state';

export interface PlayRecord {
  id: string;
  schedule_id: string | null;
  asset_id: string;
  started_at: string;
  duration_ms: number;
  completed: boolean;
}

export const MAX_BUFFER = 10_000;
export const BATCH_SIZE = 500;
export const FLUSH_THRESHOLD = 50;
const KEY = 'plays';

/** uuid v4: crypto.randomUUID se c'è, altrimenti da getRandomValues, altrimenti Math.random. */
export function newId(c: Pick<Crypto, 'randomUUID' | 'getRandomValues'> | undefined = globalThis.crypto): string {
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const b = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(b);
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = ((b[6] as number) & 0x0f) | 0x40;
  b[8] = ((b[8] as number) & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export class PlayBuffer {
  private items: PlayRecord[] = [];
  private loaded = false;
  private chain: Promise<unknown> = Promise.resolve();

  constructor(private readonly kv: KvStore, private readonly max = MAX_BUFFER) {}

  /** Serializza le operazioni (add/remove concorrenti non si pestano). */
  private run<T>(fn: () => Promise<T>): Promise<T> {
    const p = this.chain.then(fn, fn);
    this.chain = p.catch(() => undefined);
    return p;
  }

  private async ensureLoaded(): Promise<void> {
    if (this.loaded) return;
    this.items = (await this.kv.get<PlayRecord[]>(KEY)) ?? [];
    this.loaded = true;
  }

  load(): Promise<void> { return this.run(() => this.ensureLoaded()); }

  add(rec: PlayRecord): Promise<void> {
    return this.run(async () => {
      await this.ensureLoaded();
      if (this.items.some((r) => r.id === rec.id)) return; // idempotente
      this.items.push(rec);
      if (this.items.length > this.max) this.items.splice(0, this.items.length - this.max);
      await this.kv.set(KEY, this.items);
    });
  }

  async size(): Promise<number> {
    await this.run(() => this.ensureLoaded());
    return this.items.length;
  }

  async peek(n = BATCH_SIZE): Promise<PlayRecord[]> {
    await this.run(() => this.ensureLoaded());
    return this.items.slice(0, n);
  }

  remove(ids: string[]): Promise<void> {
    return this.run(async () => {
      await this.ensureLoaded();
      const gone = new Set(ids);
      this.items = this.items.filter((r) => !gone.has(r.id));
      await this.kv.set(KEY, this.items);
    });
  }
}

export interface PlaySender {
  /** Deve rifiutare se la risposta non è un 200. */
  sendPlays(batch: PlayRecord[]): Promise<void>;
}

export class PlayReporter {
  private flushing: Promise<void> | null = null;

  constructor(
    private readonly buffer: PlayBuffer,
    private readonly sender: PlaySender,
    private readonly onError: (msg: string) => void = () => undefined,
    private readonly threshold = FLUSH_THRESHOLD,
  ) {}

  /** Registra un play; oltre la soglia parte un invio in background. */
  async add(rec: PlayRecord): Promise<void> {
    await this.buffer.add(rec);
    if ((await this.buffer.size()) > this.threshold) {
      this.flush().catch((e: unknown) => this.onError(`plays: ${e instanceof Error ? e.message : String(e)}`));
    }
  }

  /** Svuota il buffer a lotti; si ferma al primo errore (il lotto resta). Rifiuta con l'errore. */
  flush(): Promise<void> {
    if (!this.flushing) {
      this.flushing = this.doFlush().finally(() => { this.flushing = null; });
    }
    return this.flushing;
  }

  private async doFlush(): Promise<void> {
    for (;;) {
      const batch = await this.buffer.peek(BATCH_SIZE);
      if (batch.length === 0) return;
      await this.sender.sendPlays(batch); // se lancia, il lotto resta nel buffer
      await this.buffer.remove(batch.map((r) => r.id));
    }
  }
}

interface Open {
  id: string;
  schedule_id: string | null;
  asset_id: string;
  startedMs: number;
  startedPerf: number;
}

export interface RecorderOptions {
  clock: { now(): number };
  perfNow?: () => number;
  /** Riceve ogni record concluso. */
  onRecord: (rec: PlayRecord) => void;
  next?: EventSink;
  makeId?: () => string;
  /** A ogni `show`: serve a "cosa è in onda adesso" (POST /api/v3/now). */
  onShow?: (info: { scheduleId: string | null; assetId: string; startedAt: string; durationMs: number | null }) => void;
  /** Quando non resta nessuno slot visibile (schermo nero). */
  onBlack?: (startedAt: string) => void;
}

/** EventSink che osserva show/hide e inoltra tutto a `next`. */
export class PlayRecorder implements EventSink {
  private readonly open = new Map<string, Open>();
  private leave: boolean | null = null;
  private readonly perfNow: () => number;

  constructor(private readonly o: RecorderOptions) {
    this.perfNow = o.perfNow ?? (() => performance.now());
  }

  /** Dal controller, prima di lasciare il contenuto: il prossimo `hide` userà questo `completed`. */
  leaving(completed: boolean): void {
    this.leave = completed;
  }

  push(type: PlayerEventType, slot: string | null, scheduleId: string | null, assetId?: string | null, durationMs?: number | null): void {
    this.o.next?.push(type, slot, scheduleId, assetId, durationMs);
    if (!slot) return;
    if (type === 'show' && assetId) {
      const startedMs = this.o.clock.now();
      this.open.set(slot, {
        id: (this.o.makeId ?? newId)(),
        schedule_id: scheduleId,
        asset_id: assetId,
        startedMs,
        startedPerf: this.perfNow(),
      });
      this.safely(() => this.o.onShow?.({ scheduleId, assetId, startedAt: new Date(startedMs).toISOString(), durationMs: durationMs ?? null }));
    } else if (type === 'hide') {
      this.close(slot, this.leave ?? false);
      this.leave = null;
      if (this.open.size === 0) this.safely(() => this.o.onBlack?.(new Date(this.o.clock.now()).toISOString()));
    }
  }

  /** Un osservatore che fallisce non deve mai rompere la riproduzione. */
  private safely(fn: () => void): void {
    try { fn(); } catch { /* ignorato */ }
  }

  private close(slot: string, completed: boolean): void {
    const op = this.open.get(slot);
    if (!op) return;
    this.open.delete(slot);
    this.o.onRecord({
      id: op.id,
      schedule_id: op.schedule_id,
      asset_id: op.asset_id,
      started_at: new Date(op.startedMs).toISOString(),
      duration_ms: Math.max(1, Math.round(this.perfNow() - op.startedPerf)),
      completed,
    });
  }

  /** Chiusura di emergenza (pagina che si chiude): i contenuti ancora aperti risultano interrotti. */
  closeAll(): void {
    for (const slot of [...this.open.keys()]) this.close(slot, false);
  }
}
